import {
  Dashboard,
  DashboardTile,
  defaultTileHeight,
  defaultTileSize,
  HEIGHT_TO_H,
  packTileLayout,
  SIZE_TO_W,
} from './dashboards'
import { personaStorageKey } from './persona'
import { ViewConfig } from './view-config'

/**
 * localStorage persistence for user-built dashboards (compositions of existing
 * reports). Mirrors saved-views.ts — prototype-only; production would be an
 * org-keyed dashboards table. A SavedDashboard is just a Dashboard plus a
 * created timestamp, so it renders through the same DashboardCard / DashboardPage
 * as the shipped ones.
 */

export const STORAGE_KEY = 'reporting-prototype.savedDashboards'

export type SavedDashboard = Dashboard & { createdAt: string }

/** A tile must at least carry a non-null config object — renderers destructure tile.config. */
const isTile = (t: unknown): boolean =>
  typeof t === 'object' &&
  t !== null &&
  typeof (t as Record<string, unknown>).config === 'object' &&
  (t as Record<string, unknown>).config !== null

/** Shape gate for deserialized entries — corrupt/foreign entries are dropped silently. */
const isSavedDashboard = (v: unknown): v is SavedDashboard => {
  if (typeof v !== 'object' || v === null) return false
  const c = v as Record<string, unknown>
  return (
    typeof c.id === 'string' &&
    typeof c.name === 'string' &&
    Array.isArray(c.tiles) &&
    c.tiles.every(isTile) &&
    typeof c.createdAt === 'string'
  )
}

export function listSavedDashboards(): SavedDashboard[] {
  try {
    const raw = localStorage.getItem(personaStorageKey(STORAGE_KEY))
    if (raw === null) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(isSavedDashboard)
  } catch {
    return []
  }
}

/**
 * Persist a new dashboard composed of existing report tiles.
 *
 * @param name - User-facing dashboard name.
 * @param tiles - The composed tiles, in display order.
 * @param description - Optional subtitle for the library card.
 * @returns The persisted SavedDashboard (with generated id + timestamp).
 */
export function saveDashboard(
  name: string,
  tiles: DashboardTile[],
  description = 'Custom dashboard.'
): SavedDashboard {
  const saved: SavedDashboard = {
    id: `dash-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name,
    description,
    icon: 'dashboard',
    tiles,
    createdAt: new Date().toISOString(),
  }
  localStorage.setItem(
    personaStorageKey(STORAGE_KEY),
    JSON.stringify([...listSavedDashboards(), saved])
  )
  return saved
}

/**
 * Update an existing saved dashboard in place (keeps its id + createdAt).
 * Returns the updated dashboard, or undefined if no saved dashboard has that id
 * (e.g. trying to overwrite a shipped one — callers should "save as new").
 */
export function updateSavedDashboard(
  id: string,
  name: string,
  tiles: DashboardTile[],
  description?: string
): SavedDashboard | undefined {
  const list = listSavedDashboards()
  const existing = list.find((d) => d.id === id)
  if (!existing) return undefined
  const updated: SavedDashboard = {
    ...existing,
    name,
    tiles,
    ...(description !== undefined && { description }),
  }
  localStorage.setItem(
    personaStorageKey(STORAGE_KEY),
    JSON.stringify(list.map((d) => (d.id === id ? updated : d)))
  )
  return updated
}

export function deleteSavedDashboard(id: string): void {
  localStorage.setItem(
    personaStorageKey(STORAGE_KEY),
    JSON.stringify(listSavedDashboards().filter((d) => d.id !== id))
  )
}

export const getSavedDashboard = (id: string): SavedDashboard | undefined =>
  listSavedDashboards().find((d) => d.id === id)

/** Build a tile for `config`, placed on a fresh row at grid `y`. */
const makeTile = (config: ViewConfig, y: number): DashboardTile => {
  const size = defaultTileSize(config)
  const height = defaultTileHeight(config)
  return {
    config,
    size,
    height,
    layout: { x: 0, y, w: SIZE_TO_W[size], h: HEIGHT_TO_H[height] },
  }
}

/**
 * Append a report as a new tile on an existing saved dashboard, dropped on a
 * fresh row below everything already placed (mirrors the builder's addTile).
 * No-op (undefined) for a shipped dashboard — only "Mine" dashboards are editable.
 */
export function addReportToDashboard(
  id: string,
  config: ViewConfig
): SavedDashboard | undefined {
  const dash = getSavedDashboard(id)
  if (!dash) return undefined
  // Saved tiles carry a layout; fall back to packed positions for any that don't.
  const packed = packTileLayout(dash.tiles)
  const y = dash.tiles.reduce(
    (max, t, i) =>
      Math.max(
        max,
        (t.layout?.y ?? packed[i].y) + (t.layout?.h ?? packed[i].h)
      ),
    0
  )
  return updateSavedDashboard(id, dash.name, [
    ...dash.tiles,
    makeTile(config, y),
  ])
}

/** Create a new saved dashboard seeded with a single report tile. */
export const createDashboardFromReport = (
  name: string,
  config: ViewConfig,
  description?: string
): SavedDashboard => saveDashboard(name, [makeTile(config, 0)], description)
